import Cookies from 'js-cookie';
import { useState } from 'react';
import Cross from '../../../icons/Cross';
import colorConfigs from '../../../configs/colorConfigs';
import { useTheme } from '../../../components/hooks/useTheme';
import { Button, Dialog } from '@mui/material';
import axios from 'axios';
import { ticketBaseURL } from '../../../configs/api';
import useConfirm from '../../../components/hooks/useConfirm';
import useAuth from '../../../components/hooks/useAuth';
import { post_RdRentalAchInfo } from '../../../services/ACHInfo';
import useTenant from '../../../components/hooks/useTenant';

type ACHProps = {
    showPopupAddNewACH: boolean;
    setShowPopupAddNewACH: (value: boolean) => void;
    setIsRefresh: (value: boolean) => void;
}

const AddACH = ({ showPopupAddNewACH, setShowPopupAddNewACH, setIsRefresh }: ACHProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const { themeStyles, theme } = useTheme();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const userId = Cookies.get("userId-jj-rental-ui");

    const [achDetails, setACHDetails] = useState<any>({
        accountNumber: "",
        accountType: "",
        routingNumber: "",
        bankName: "",
        bankPhone: "",
        accountHolderName: "",
        useAccountInfoInFuture: false,
        signedAcknowledgement: false
    });
    const textColor =
        theme === "dark"
            ? `${themeStyles.main.textSubHeadingColor}`
            : `${themeStyles.main.textHeadingColor}`;

    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isProcessing) return;

        setIsProcessing(true);

        try {
            const response = await axios.get(
                `${ticketBaseURL}rdRentalACHInfo/DoesRdRentalACHInfoExist`,
                {
                    params: {
                        accountNumber: achDetails?.accountNumber,
                        routingNumber: achDetails?.routingNumber,
                        tenantId: tenantId
                    }
                }
            );

            const cardExists = response.data === true;

            if (cardExists) {
                setConfirmData({
                    title: 'Warning',
                    summary: "This ACH is already added for this group.",
                    confirmBtn: 'Ok',
                    isConfirm: (callback: any) => {
                        callback();
                    }
                });

                setConfirmPopup(true);

                setIsProcessing(false);
                return;
            }

            await post_RdRentalAchInfo({
                ...achDetails,
                isActive: true,
                userId: Number(userId),
                tenantId: tenantId
            }, auth?.id);

            setIsRefresh(true);
            setShowPopupAddNewACH(false);
            setACHDetails({
                accountNumber: "",
                accountType: "",
                routingNumber: "",
                bankName: "",
                bankPhone: "",
                accountHolderName: "",
                useAccountInfoInFuture: false,
                signedAcknowledgement: false
            });
            handleTenantError(null);
        } catch (error) {
            handleTenantError(error);
        } finally {
            setIsProcessing(false);
        }
    };

    const closeModal = () => setShowPopupAddNewACH(false);

    return (
        <Dialog
            open={showPopupAddNewACH}
            sx={{
                '& .MuiDialog-paper': {
                    maxWidth: '800px',
                    width: '100%',
                    borderRadius: '16px',
                },
            }}
        >
            <div className={`fixed inset-0 p-4 mt-20 ${showPopupAddNewACH ? 'flex' : 'hidden'} flex-wrap justify-center items-center w-full h-fit z-50 overflow-auto`}>
                <div className={`w-full max-w-[32rem] shadow-lg rounded-md p-5 relative ${themeStyles.main.tailwindMainBg}`}>
                    <Cross closeModal={closeModal} className={`w-3 h-3 absolute top-2 right-2 cursor-pointer ${textColor}`} />
                    <div style={{ width: "100%", marginTop: "1.25rem" }}>
                        <form className="mt-1 flex flex-col gap-4" onSubmit={handleSubmit}>
                            <div className="flex flex-col">
                                <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>
                                    Account Holder Name
                                </label>
                                <input
                                    type="text"
                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                    value={achDetails.accountHolderName}
                                    onChange={(e) =>
                                        setACHDetails({ ...achDetails, accountHolderName: e.target.value })
                                    }
                                    required
                                />
                            </div>

                            <div className="flex flex-col">
                                <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>
                                    Account Number
                                </label>
                                <input
                                    type="text"
                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                    value={achDetails.accountNumber}
                                    onChange={(e) =>
                                        setACHDetails({ ...achDetails, accountNumber: e.target.value })
                                    }
                                    required
                                />
                            </div>

                            <div className="flex flex-col">
                                <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>
                                    Routing Number
                                </label>
                                <input
                                    type="text"
                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                    value={achDetails.routingNumber}
                                    onChange={(e) =>
                                        setACHDetails({ ...achDetails, routingNumber: e.target.value })
                                    }
                                    required
                                />
                            </div>

                            <div className="flex flex-col">
                                <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>
                                    Bank Name
                                </label>
                                <input
                                    type="text"
                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                    value={achDetails.bankName}
                                    onChange={(e) =>
                                        setACHDetails({ ...achDetails, bankName: e.target.value })
                                    }
                                />
                            </div>

                            <div className="flex flex-col">
                                <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>
                                    Bank Phone
                                </label>
                                <input
                                    type="text"
                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                    value={achDetails.bankPhone}
                                    onChange={(e) =>
                                        setACHDetails({ ...achDetails, bankPhone: e.target.value })
                                    }
                                />
                            </div>

                            <div className="flex flex-col">
                                <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>
                                    Account Type
                                </label>
                                <select
                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                    value={achDetails.accountType}
                                    onChange={(e) =>
                                        setACHDetails({ ...achDetails, accountType: e.target.value })
                                    }
                                    required
                                >
                                    <option value="">Select</option>
                                    <option value="checking">Checking</option>
                                    <option value="savings">Savings</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="useAccountInfoInFuture"
                                    checked={achDetails.useAccountInfoInFuture}
                                    onChange={(e) =>
                                        setACHDetails({
                                            ...achDetails,
                                            useAccountInfoInFuture: e.target.checked
                                        })
                                    }
                                />
                                <label htmlFor="useAccountInfoInFuture" className={`text-xs ${themeStyles.form.tailwindLabelTextColor}`}>
                                    Use account info in future
                                </label>
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="signedAcknowledgement"
                                    checked={achDetails.signedAcknowledgement}
                                    onChange={(e) =>
                                        setACHDetails({
                                            ...achDetails,
                                            signedAcknowledgement: e.target.checked
                                        })
                                    }
                                    required
                                />
                                <label htmlFor="signedAcknowledgement" className={`text-xs ${themeStyles.form.tailwindLabelTextColor}`}>
                                    I acknowledge and authorize ACH transactions
                                </label>
                            </div>

                            <div className="pt-4">
                                <Button
                                    variant="contained"
                                    sx={{ backgroundColor: colorConfigs.btn.main.bg }}
                                    type="submit"
                                >
                                    {isProcessing ? "Processing..." : "Save"}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </Dialog>
    );
}

export default AddACH;